import type { AxiosInstance } from "axios";
import { BarChart3, ChevronDown, ChevronRight, ChevronsUpDown } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { DateRangePicker } from "../components/DateRangePicker.js";
import { FormulaList, HighlightCard, HighlightGrid, noteText } from "../components/ReportGuide/GuideContent.js";
import { ReportGuideAccordion } from "../components/ReportGuide/ReportGuideAccordion.js";
import { ReportGuideToggleButton } from "../components/ReportGuide/ReportGuideToggleButton.js";
import { Select } from "../components/ui/select.js";
import { Skeleton } from "../components/ui/skeleton.js";
import { apiErrorMessage } from "../lib/api.js";
import { cn } from "../lib/cn.js";
import { TableResizeHandle, useTableResize } from "../components/TableResizeHandle.js";
import { rangeProblem, toLocalDate, type DateRange } from "../lib/dates.js";

/**
 * Campaign Cohort LTV Analysis. Ported from `ecom_code`'s `CohortLtv.jsx` +
 * `getCohortLtv` — same tree (creation-month banner -> campaign summary -> monthly
 * acquisition cohorts), same M0..Mn purchaser-offset columns, same 3-state "New
 * Visitors" sort. The table, type scale and colour tokens are the platform's.
 */

interface CohortEntry {
  readonly entry_month: string;
  readonly month_label: string;
  readonly new_visitors: number;
  readonly total_purchasers: number;
  readonly offsets: Readonly<Record<string, number>>;
}

interface CohortCampaign {
  readonly campaign_id: string;
  readonly campaign_name: string;
  readonly cohort_month: string;
  readonly total_new_visitors: number;
  readonly total_purchasers: number;
  readonly offsets: Readonly<Record<string, number>>;
  readonly months: readonly CohortEntry[];
}

interface CohortMonthGroup {
  readonly cohort_month: string;
  readonly month_name: string;
  readonly campaigns: readonly CohortCampaign[];
}

interface CohortLtvResponse {
  readonly platform: "meta" | "google";
  readonly from: string;
  readonly to: string;
  readonly groups: readonly CohortMonthGroup[];
}

type SortDirection = "desc" | "asc" | null;

/** Jan 1 of the current year -> today, matching `ecom_code`'s default window. */
function defaultCohortRange(today: Date = new Date()): DateRange {
  return { from: `${String(today.getFullYear())}-01-01`, to: toLocalDate(today) };
}

const monthsBetween = (fromYm: string, toYm: string): number => {
  const [fy, fm] = fromYm.split("-").map(Number);
  const [ty, tm] = toYm.split("-").map(Number);
  if (fy === undefined || fm === undefined || ty === undefined || tm === undefined) return 0;
  return Math.max(0, (ty - fy) * 12 + (tm - fm));
};

const addMonths = (ym: string, n: number): string => {
  const [y, m] = ym.split("-").map(Number);
  if (y === undefined || m === undefined) return ym;
  const total = (m - 1) + n;
  const year = y + Math.floor(total / 12);
  const month = (total % 12) + 1;
  return `${String(year)}-${String(month).padStart(2, "0")}`;
};

const fmt = (n: number): string => (n === 0 ? "—" : n.toLocaleString("en-US"));

export function CampaignCohortLtvScreen({
  client,
  tenantId,
  storeName,
}: {
  readonly client: AxiosInstance;
  readonly tenantId: string;
  readonly storeName: string;
}): React.JSX.Element {
  const [range, setRange] = useState<DateRange>(() => defaultCohortRange());
  const [platform, setPlatform] = useState<"meta" | "google">("meta");
  const [data, setData] = useState<CohortLtvResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [showGuideSection, setShowGuideSection] = useState(false);

  const problem = useMemo(() => rangeProblem(range), [range]);

  const load = useCallback(async () => {
    if (problem !== null) return;
    setLoading(true);
    setError(null);
    try {
      const res = await client.get<{ success: boolean; data: CohortLtvResponse }>("/api/v2/cohortltv/campaign", {
        params: { from: range.from, to: range.to, platform },
      });
      setData(res.data.data);
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not load the cohort report. Check that this platform is connected."));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [client, range.from, range.to, platform, problem]);

  useEffect(() => {
    void load();
    setExpanded(new Set());
  }, [load, tenantId]);

  const toggle = (campaignId: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(campaignId)) next.delete(campaignId);
      else next.add(campaignId);
      return next;
    });
  };

  const toMonth = range.to.slice(0, 7);

  // 3-state sort: desc -> asc -> none, on total_new_visitors — legacy's exact cycle.
  const groups = useMemo(() => {
    const raw = data?.groups ?? [];
    if (sortDirection === null) return raw;
    const sign = sortDirection === "desc" ? -1 : 1;
    return raw
      .map((g) => ({
        ...g,
        campaigns: [...g.campaigns].sort((a, b) => sign * (a.total_new_visitors - b.total_new_visitors)),
      }))
      .sort((a, b) => {
        const aTotal = a.campaigns.reduce((s, c) => s + c.total_new_visitors, 0);
        const bTotal = b.campaigns.reduce((s, c) => s + c.total_new_visitors, 0);
        return sign * (aTotal - bTotal);
      });
  }, [data, sortDirection]);

  // How many M columns to show: the report window's span, or a wider one the data needs.
  const maxOffset = useMemo(() => {
    const windowSpan = monthsBetween(range.from.slice(0, 7), toMonth);
    let dataMax = 0;
    for (const g of groups) {
      for (const c of g.campaigns) {
        for (const key of Object.keys(c.offsets)) dataMax = Math.max(dataMax, Number(key));
      }
    }
    return Math.max(windowSpan, dataMax);
  }, [groups, range.from, toMonth]);

  const offsetCols = Array.from({ length: maxOffset + 1 }, (_, i) => i);

  const initialWidths = useMemo(
    () => [280, 140, ...offsetCols.map(() => 90), 140],
    [offsetCols.length],
  );
  const { colWidths, startResize } = useTableResize(initialWidths);

  const handleSortToggle = (): void => {
    setSortDirection((prev) => (prev === null ? "desc" : prev === "desc" ? "asc" : null));
  };

  const isEmpty = !loading && groups.length === 0;

  return (
    <section className="flex min-w-0 flex-col gap-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2.5 text-2xl font-bold tracking-tight">
            <BarChart3 className="text-primary size-6" />
            Campaign Cohort LTV Analysis
            <ReportGuideToggleButton
              showGuide={showGuideSection}
              onToggle={() => setShowGuideSection((current) => !current)}
            />
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Campaigns grouped by creation month, with visitor acquisition and purchaser cohorts over time (M0, M1, M2…)
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <DateRangePicker value={range} onApply={setRange} />
          <Select
            value={platform}
            onChange={(e) => setPlatform(e.target.value as "meta" | "google")}
            className="h-9 w-32 text-xs font-medium"
            aria-label="Ad platform"
          >
            <option value="meta">Meta</option>
            <option value="google">Google</option>
          </Select>
        </div>
      </header>

      {/*
        Guide content mirrors `ecom_code`'s `CohortLtvReportNotes`/`CohortLtvCalculationNotes`
        closely — same "Month Groups & Campaigns" / "M0, M1, M2..." pair of cards, same
        New Visitors / M-column / Total Purchasers breakdown. Nothing to adapt here: this
        screen's tree (creation-month banner -> campaign summary -> monthly cohorts) is a
        direct port of legacy's, per `CampaignCohortLtvScreen`'s own doc comment.
      */}
      <ReportGuideAccordion
        showGuide={showGuideSection}
        whatContent={
          <div className={noteText}>
            <p className="m-0">
              Campaign Cohort LTV Analysis groups your campaigns by the <strong>month they were created</strong>, then
              tracks how the visitors each campaign acquired keep purchasing in the months that follow:
            </p>
            <HighlightGrid cols={2}>
              <HighlightCard tone="blue" title="1. Month Groups & Campaigns">
                Rows are grouped under a 📅 month banner (the campaign&apos;s creation month). Click a campaign row to
                expand it and reveal its individual acquisition-month cohorts.
              </HighlightCard>
              <HighlightCard tone="purple" title="2. M0, M1, M2... Columns">
                Each M column is a month offset from that cohort&apos;s acquisition month — M0 is the acquisition month
                itself, M1 is the following month, and so on — showing how purchases from that same visitor group
                continue over time.
              </HighlightCard>
            </HighlightGrid>
          </div>
        }
        howTitle="How cohorts & purchaser totals are calculated"
        howContent={
          <div className={noteText}>
            <p className="m-0">How the cohort table is built and aggregated:</p>
            <FormulaList>
              <li>
                <strong>New visitors:</strong> on a campaign summary row this is the total new visitors acquired by that
                campaign across all its acquisition-month cohorts; on an expanded acquisition-month row it&apos;s the
                new visitors acquired specifically in that month.
              </li>
              <li>
                <strong>M0 – M{"{n}"} purchaser columns:</strong> for each acquisition-month cohort, M0 counts
                purchasers who bought in their entry month, M1 counts purchasers from that same cohort who bought the
                following month, etc. Campaign summary rows show the sum of that column across all of the campaign&apos;s
                cohorts.
              </li>
              <li>
                <strong>Total purchasers:</strong> the unique count of purchasers acquired in a cohort, summed across
                all months tracked (M0 through the latest available offset for the selected date range) — not a sum of
                the M columns, since a purchaser who bought in both M0 and M1 is counted once.
              </li>
              <li>An offset column is only populated once that many months have actually elapsed since the cohort month.</li>
            </FormulaList>
          </div>
        }
      />

      {problem !== null && (
        <p className="text-destructive text-sm" role="alert">
          {problem}
        </p>
      )}
      {error !== null && (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      )}

      <hr className="border-border" />

      <h2 className="text-foreground text-base font-bold">Cohort LTV — Campaign Creation Month × Acquisition Cohorts</h2>

      <div className="bg-card min-w-0 overflow-hidden rounded-xl border shadow-sm">
        <div className="max-h-[min(70svh,48rem)] overflow-auto">
          <table className="w-full table-fixed border-separate border-spacing-0 text-sm">
            <colgroup>
              {colWidths.map((w, i) => (
                <col key={i} style={{ width: `${w}px` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th className="row-head text-foreground sticky top-0 left-0 z-30 h-11 border-b border-r border-border px-3 text-left text-[13px] font-semibold relative">
                  Acquisition cohort
                  <TableResizeHandle onResize={(e) => startResize(0, e)} />
                </th>
                <th className="row-head text-foreground sticky top-0 z-20 h-11 border-b border-r border-border px-3 text-center text-[13px] font-semibold relative">
                  <button type="button" onClick={handleSortToggle} className="hover:text-primary inline-flex items-center gap-1">
                    New visitors
                    {sortDirection === "desc" ? (
                      <ChevronDown className="size-3.5" />
                    ) : sortDirection === "asc" ? (
                      <ChevronRight className="size-3.5 -rotate-90" />
                    ) : (
                      <ChevronsUpDown className="size-3 opacity-40" />
                    )}
                  </button>
                  <TableResizeHandle onResize={(e) => startResize(1, e)} />
                </th>
                {offsetCols.map((i, idx) => (
                  <th
                    key={i}
                    className="row-head text-foreground sticky top-0 z-20 h-11 border-b border-r border-border px-3 text-center text-[13px] font-semibold relative"
                  >
                    M{i}
                    <TableResizeHandle onResize={(e) => startResize(2 + idx, e)} />
                  </th>
                ))}
                <th className="row-head text-foreground sticky top-0 z-20 h-11 border-b border-r border-border px-3 text-center text-[13px] font-semibold relative">
                  Total purchasers
                  <TableResizeHandle onResize={(e) => startResize(2 + offsetCols.length, e)} />
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }, (_, i) => (
                  <tr key={i} className={i % 2 === 1 ? "row-odd" : "row-even"}>
                    <td className="border-b px-3 py-3">
                      <Skeleton className="h-4 w-56" />
                    </td>
                    {Array.from({ length: offsetCols.length + 2 }, (_, j) => (
                      <td key={j} className="border-b px-3 py-3 text-center">
                        <Skeleton className="mx-auto h-4 w-10" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : isEmpty ? (
                <tr>
                  <td colSpan={offsetCols.length + 3} className="text-muted-foreground px-4 py-14 text-center text-sm">
                    No campaigns found for {storeName} in this period and platform.
                  </td>
                </tr>
              ) : (
                groups.map((group) => (
                  <CohortGroup
                    key={group.cohort_month}
                    group={group}
                    offsetCols={offsetCols}
                    toMonth={toMonth}
                    expanded={expanded}
                    onToggle={toggle}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function CohortGroup({
  group,
  offsetCols,
  toMonth,
  expanded,
  onToggle,
}: {
  readonly group: CohortMonthGroup;
  readonly offsetCols: readonly number[];
  readonly toMonth: string;
  readonly expanded: ReadonlySet<string>;
  readonly onToggle: (campaignId: string) => void;
}): React.JSX.Element {
  return (
    <>
      <tr className="bg-muted/60 border-t border-b">
        <td colSpan={offsetCols.length + 3} className="text-foreground px-3 py-2 text-sm font-bold">
          📅 {group.month_name}
        </td>
      </tr>
      {group.campaigns.map((camp) => (
        <CampaignRows
          key={camp.campaign_id}
          camp={camp}
          offsetCols={offsetCols}
          toMonth={toMonth}
          isOpen={expanded.has(camp.campaign_id)}
          onToggle={onToggle}
        />
      ))}
    </>
  );
}

function CampaignRows({
  camp,
  offsetCols,
  toMonth,
  isOpen,
  onToggle,
}: {
  readonly camp: CohortCampaign;
  readonly offsetCols: readonly number[];
  readonly toMonth: string;
  readonly isOpen: boolean;
  readonly onToggle: (campaignId: string) => void;
}): React.JSX.Element {
  return (
    <>
      <tr className="hover:bg-accent/50 border-b transition-colors">
        <td className="px-3 py-2.5">
          <button
            type="button"
            onClick={() => onToggle(camp.campaign_id)}
            className="text-foreground flex w-full items-center gap-1.5 text-left text-xs font-semibold"
          >
            <ChevronRight className={cn("text-muted-foreground size-3.5 shrink-0 transition-transform", isOpen && "rotate-90")} />
            <span className="truncate" title={camp.campaign_name}>
              {camp.campaign_name}
            </span>
          </button>
        </td>
        <td className="px-3 py-2.5 text-center text-xs font-bold">{fmt(camp.total_new_visitors)}</td>
        {offsetCols.map((i) => {
          const v = camp.offsets[String(i)] ?? 0;
          return (
            <td key={i} className="px-3 py-2.5 text-center text-xs">
              {v > 0 ? <span className="text-primary font-bold">{fmt(v)}</span> : <span className="text-muted-foreground">—</span>}
            </td>
          );
        })}
        <td className="px-3 py-2.5 text-center text-xs font-bold text-emerald-600">{fmt(camp.total_purchasers)}</td>
      </tr>

      {isOpen &&
        camp.months.map((m) => (
          <tr key={m.entry_month} className="bg-muted/20 border-b">
            <td className="text-muted-foreground px-3 py-2 pl-10 text-xs italic">{m.month_label}</td>
            <td className="px-3 py-2 text-center text-xs">{fmt(m.new_visitors)}</td>
            {offsetCols.map((i) => {
              const targetMonth = addMonths(m.entry_month, i);
              if (targetMonth > toMonth) {
                return (
                  <td key={i} className="text-muted-foreground/50 px-3 py-2 text-center text-xs">
                    —
                  </td>
                );
              }
              const v = m.offsets[String(i)] ?? 0;
              return (
                <td key={i} className="px-3 py-2 text-center text-xs">
                  {v > 0 ? <span className="text-primary font-medium">{fmt(v)}</span> : <span className="text-muted-foreground">—</span>}
                </td>
              );
            })}
            <td className="px-3 py-2 text-center text-xs font-semibold text-emerald-600">{fmt(m.total_purchasers)}</td>
          </tr>
        ))}
    </>
  );
}
